/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useMemo, useState } from 'react';
import {
  Box,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { TeamAvatar } from '../home/TeamAvatar';
import { READ_ONLY_REF } from './ViewAsContext';

const displayNameOf = (user: Entity): string =>
  (user.spec?.profile as { displayName?: string } | undefined)?.displayName ??
  user.metadata.title ??
  user.metadata.name;

const pictureOf = (user: Entity | undefined): string | undefined =>
  (user?.spec?.profile as { picture?: string } | undefined)?.picture;

const useStyles = makeStyles(theme => ({
  paper: {
    width: 320,
    maxHeight: 480,
    padding: theme.spacing(0.5, 0),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: { padding: theme.spacing(1, 1.5, 0.5) },
  section: { overflowY: 'auto', flex: 1 },
  row: { padding: theme.spacing(0.75, 1.5) },
  empty: { padding: theme.spacing(1.5) },
  avatarGap: { minWidth: 32 },
}));

/**
 * Searchable "view as" user picker, anchored to an external trigger (Home
 * toolbar, sidebar item, …). Lazy-loads the User catalog on first open.
 */
export const ViewAsPicker = ({
  anchorEl,
  open,
  activeRef,
  onPick,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  open: boolean;
  activeRef: string | null;
  onPick: (ref: string | null) => void;
  onClose: () => void;
}) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<Entity[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  // Lazy-load on first open; never as part of the host's initial render.
  React.useEffect(() => {
    if (!open || users !== null || loadFailed) return undefined;
    let active = true;
    catalogApi
      .getEntities({
        filter: { kind: 'User' },
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'spec.profile',
        ],
      })
      .then(res => {
        if (!active) return;
        setUsers(
          [...res.items].sort((a, b) =>
            displayNameOf(a).localeCompare(displayNameOf(b)),
          ),
        );
      })
      .catch(() => active && setLoadFailed(true));
    return () => {
      active = false;
    };
  }, [open, users, loadFailed, catalogApi]);

  const close = () => {
    setQuery('');
    onClose();
  };
  const pick = (ref: string | null) => {
    onPick(ref);
    close();
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      u =>
        displayNameOf(u).toLowerCase().includes(q) ||
        u.metadata.name.toLowerCase().includes(q),
    );
  }, [users, query]);

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={close}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ className: classes.paper }}
    >
      <Box className={classes.header}>
        <TextField
          inputRef={(el: HTMLInputElement | null) => el?.focus()}
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Search users"
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          inputProps={{ 'aria-label': 'Search users to view as' }}
        />
      </Box>
      <Box className={classes.section}>
        {activeRef && (
          <MenuItem className={classes.row} onClick={() => pick(null)}>
            <ListItemIcon className={classes.avatarGap}>
              <CloseIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Back to my own view" />
          </MenuItem>
        )}
        {/* Synthetic identity: preview the read-only fallback (no teams, no
            owned services, no gated items) without a real user. */}
        <MenuItem
          className={classes.row}
          selected={activeRef === READ_ONLY_REF}
          onClick={() => pick(READ_ONLY_REF)}
        >
          <ListItemIcon className={classes.avatarGap}>
            <LockOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Read-only user"
            secondary="No teams or access"
          />
        </MenuItem>
        {users === null && !loadFailed && (
          <Box className={classes.empty}>
            <Typography variant="body2" color="textSecondary">
              Loading users…
            </Typography>
          </Box>
        )}
        {loadFailed && (
          <Box className={classes.empty}>
            <Typography variant="body2" color="error">
              Couldn't load users.
            </Typography>
          </Box>
        )}
        {users !== null && filtered.length === 0 && (
          <Box className={classes.empty}>
            <Typography variant="body2" color="textSecondary">
              No users match “{query.trim()}”.
            </Typography>
          </Box>
        )}
        {filtered.map(u => {
          const ref = stringifyEntityRef(u);
          const name = displayNameOf(u);
          // Only show the login as a secondary line when it adds information
          // (users without a profile displayName fall back to the login, so
          // showing it twice is just noise).
          const secondary = name === u.metadata.name ? undefined : u.metadata.name;
          return (
            <MenuItem
              key={ref}
              className={classes.row}
              selected={ref === activeRef}
              onClick={() => pick(ref)}
            >
              <ListItemIcon className={classes.avatarGap}>
                <TeamAvatar
                  slug={u.metadata.name}
                  pictureUrl={pictureOf(u)}
                  size={22}
                />
              </ListItemIcon>
              <ListItemText primary={name} secondary={secondary} />
            </MenuItem>
          );
        })}
      </Box>
    </Popover>
  );
};
